import { getNumberRegex } from "../utils/functions";
import {
  caratTypeRegex,
  isValidDecimal,
  nonZeroNumber,
  passwordRegex,
  validDecimalUptoTwoDigits,
} from "../utils/regex";

export const loaderPath = {
  loadingChangePassword: "settings-change-password",
  materials: "materials",
  addMaterial: "add-material",
  materialStatus: "material-status",
  uploadImage: "upload-image",
  shapeCuts: "shape-cuts",
  shapeCutStatus: "shape-cut-status",
  addShapeCut: "add-shape-cut",
  metalCaratTypes: "metal-carat-types",
  metalCaratTypeStatus: "metal-carat-type-status",
  addMetalCaratType: "add-metal-carat-type",
  getProductType: "get-product-type",
  getProductTypeStatus: "get-product-type-status",
  getProductColor: "get-product-color",
  getProductColorStatus: "get-product-color-status",
  addEditModal: "add-edit-modal",
  getMusharkaDuration: "get-musharka-duration",
  addMusharkaDuration: "add-musharka-duration",
  addRiskLevel: "add-risk-level",
  getRiskLevelStatus: "get-risk-level-status",
  stoneClarity: "stone-clarity",
  addStoneClarity: "add-stone-clarity",
  stoneClarityStatus: "stone-clarity-status",
  getMusharakahMaterial: "get-musharakah-material",
  addMusharakahMaterial: "add-musharakah-material",
};

export const defaultValues = {
  old_password: "",
  new_password: "",
  confirm_password: "",
};

const durationChoiceOptions = [
  { label: "week", value: "week" },
  { label: "month", value: "month" },
  { label: "year", value: "year" },
];

const riskLevelOptions = [
  { label: "low", value: "LOW" },
  { label: "medium", value: "MEDIUM" },
  { label: "high", value: "HIGH" },
];

const stoneOriginOptions = [
  { label: "natural", value: "NATURAL" },
  { label: "lab-grown", value: "LAB_GROWN" },
];

export const durationColumns = [
  { key: "name", label: "name" },
  { key: "is_active", label: "status" },
  { key: "risk_level_assign", label: "assign-risk-level" },
  { key: "action", label: "action" },
  { key: "edit", label: "edit" },
];

export const editRiskLevelField = [
  {
    type: "autocomplete",
    name: "risk_level",
    label: "risk-level",
    placeholder: "risk-level-placeholder",
    isMultiple: true,
    size: { xs: 12 },
  },
];

export const fields = [
  {
    type: "password",
    name: "old_password",
    label: "old-password",
    placeholder: "old-password",
    registerOptions: {
      required: { value: true, message: "old-password-required-error" },
    },
  },
  {
    type: "password",
    name: "new_password",
    label: "new-password",
    placeholder: "new-password",
    registerOptions: {
      required: { value: true, message: "new-password-required-error" },
      minLength: { value: 8, message: "password-min-length-error" },
      pattern: { value: passwordRegex, message: "password-pattern-error" },
    },
  },
  {
    type: "password",
    name: "confirm_password",
    label: "confirm-password",
    placeholder: "confirm-password",
    registerOptions: {
      required: { value: true, message: "confirm-password-required-error" },
    },
  },
];

export const stoneTableColumns = [
  { key: "name", label: "stone-name" },
  { key: "materialType", label: "material-type" },
  { key: "stone_origin", label: "stone-origin" },
  { key: "status", label: "status" },
  { key: "action", label: "action" },
  { key: "edit", label: "edit" },
];

export const metalTableColumns = [
  { key: "name", label: "metal-name" },
  { key: "materialType", label: "material-type" },
  { key: "status", label: "status" },
  { key: "action", label: "action" },
  { key: "edit", label: "edit" },
];

export const shapeCutTableColumns = [
  { key: "name", label: "shape-cut-name" },
  { key: "status", label: "status" },
  { key: "action", label: "action" },
  { key: "edit", label: "edit" },
];

export const metalCaratTypeTableColumns = [
  { key: "name", label: "carat-name" },
  { key: "purity", label: "purity" },
  { key: "status", label: "status" },
  { key: "action", label: "action" },
  { key: "edit", label: "edit" },
];

export const clarityTableColumns = [
  { key: "name", label: "clarity-name" },
  { key: "status", label: "status" },
  { key: "action", label: "action" },
  { key: "edit", label: "edit" },
];

export const addMaterialDefaultValues = {
  name: "",
  image: null,
  material_type: "stone",
};

export const addMaterialsFields = [
  {
    name: "name",
    formLabel: "material-name",
    placeholder: "material-name-placeholder",
    type: "text",
    registerOptions: {
      required: { value: true, message: "material-name-required-error" },
      maxLength: { value: 100, message: "material-name-max-length-error" },
      validate: {
        noLeadingSpecialChar: (value) =>
          /^[a-zA-Z0-9]/.test(value) || "name-leading-special-char-error",
      },
    },
    size: { xs: 12 },
  },
  {
    name: "stone_origin",
    formLabel: "stone-origin",
    placeholder: "stone-origin-placeholder",
    type: "select",
    options: stoneOriginOptions,
    registerOptions: {
      required: { value: true, message: "stone-origin-required-error" },
    },
    size: { xs: 12 },
  },
];

export const addShapeCutFields = [
  {
    name: "name",
    formLabel: "shape-cut-name",
    placeholder: "shape-cut-name-placeholder",
    type: "text",
    registerOptions: {
      required: { value: true, message: "shape-cut-name-required-error" },
      maxLength: { value: 100, message: "shape-cut-name-max-length-error" },
      validate: {
        noLeadingSpecialChar: (value) =>
          /^[a-zA-Z0-9]/.test(value) || "name-leading-special-char-error",
      },
    },
    size: { xs: 12 },
  },
];

export const addCaratTypeFields = [
  {
    type: "number",
    name: "name",
    alternateName: "carat-type-name",
    formLabel: "carat-type-name",
    placeholder: "carat-type-name-placeholder",
    registerOptions: {
      required: { value: true, message: "carat-type-name-required-error" },
      maxLength: { value: 2, message: "carat-type-name-max-length-error" },
      min: { value: 1, message: "carat-type-min-error" },
      pattern: { value: caratTypeRegex, message: "invalid-carat-type" },
    },
    endAdornment: "k",
    size: { xs: 12 },
  },
  {
    type: "text",
    name: "purity_percentage",
    formLabel: "purity",
    placeholder: "purity-placeholder",
    registerOptions: {
      required: { value: true, message: "purity-required-error" },
      min: { value: 1, message: "purity-min-error" },
      max: { value: 100, message: "purity-max-error" },
    },
    size: { xs: 12 },
  },
];

export const productTypeTableColumns = [
  { key: "name", label: "name" },
  { key: "status", label: "status" },
  { key: "action", label: "action" },
  { key: "edit", label: "edit" },
];

export const addProductTypeFields = [
  {
    type: "text",
    name: "name",
    formLabel: "product-type-name",
    placeholder: "product-type-name-placeholder",
    registerOptions: {
      required: { value: true, message: "product-type-name-required-error" },
      maxLength: { value: 100, message: "product-type-name-max-length-error" },
      validate: {
        noLeadingSpecialChar: (value) =>
          /^[a-zA-Z0-9]/.test(value) || "name-leading-special-char-error",
      },
    },
    size: { xs: 12 },
  },
];

export const productColorTableColumns = [
  { key: "name", label: "name" },
  { key: "status", label: "status" },
  { key: "action", label: "action" },
  { key: "edit", label: "edit" },
];

export const addProductColorFields = [
  {
    type: "text",
    name: "name",
    formLabel: "product-color-name",
    placeholder: "product-color-name-placeholder",
    registerOptions: {
      required: { value: true, message: "product-color-name-required-error" },
      maxLength: { value: 100, message: "product-color-name-max-length-error" },
      validate: {
        noLeadingSpecialChar: (value) =>
          /^[a-zA-Z0-9]/.test(value) || "name-leading-special-char-error",
      },
    },
    size: { xs: 12 },
  },
];

export const durationChoiceFields = [
  {
    type: "number",
    name: "number",
    formLabel: "duration-value-label",
    placeholder: "duration-value-placeholder",
    registerOptions: {
      required: { value: true, message: "duration-value-required-error" },
      maxLength: { value: 2, message: "duration-value-max-error" },
      pattern: {
        value: nonZeroNumber,
        message: "non-zero-amount-validation",
      },
    },
    size: { xs: 6 },
  },
  {
    type: "select",
    name: "name",
    formLabel: "duration-unit-label",
    placeholder: "duration-unit-placeholder",
    options: durationChoiceOptions,
    registerOptions: {
      required: { value: true, message: "duration-unit-required-error" },
    },
    size: { xs: 6 },
  },
  {
    type: "autocomplete",
    name: "risk_level",
    formLabel: "risk-level",
    placeholder: "risk-level-placeholder",
    isMultiple: true,
    size: { xs: 12 },
    registerOptions: {
      required: { value: true, message: "risk-level-required-error" },
    },
  },
];

export const riskLevelTableColumns = [
  { key: "risk_level", label: "risk-level" },
  { key: "penalty_amount", label: "penalty-amount" },
  { key: "equity_min", label: "equity-min" },
  { key: "equity_max", label: "equity-max" },
  { key: "max_musharakah_weight", label: "musharakah-max-weight" },
  { key: "edit", label: "edit" },
];

export const riskLevelFields = [
  {
    type: "select",
    name: "risk_level",
    formLabel: "risk-level",
    placeholder: "risk-level-placeholder",
    options: riskLevelOptions,
    registerOptions: {
      required: { value: true, message: "risk-level-required-error" },
    },
    size: { xs: 12 },
  },
  {
    type: "number",
    name: "penalty_amount",
    formLabel: "penalty-amount",
    placeholder: "penalty-amount-placeholder",
    registerOptions: {
      required: { value: true, message: "penalty-amount-required-error" },

      pattern: {
        value: getNumberRegex(9),
        message: "amount-max-length-error",
      },
    },
    size: { xs: 12 },
  },
  {
    type: "number",
    name: "equity_min",
    formLabel: "equity-min",
    placeholder: "equity-min-placeholder",
    registerOptions: {
      required: { value: true, message: "equity-min-required-error" },
      pattern: {
        value: validDecimalUptoTwoDigits,
        message: "valid-decimal-error",
      },
    },
    size: { xs: 12 },
  },
  {
    type: "number",
    name: "equity_max",
    formLabel: "equity-max",
    placeholder: "equity-max-placeholder",
    registerOptions: {
      required: { value: true, message: "equity-max-required-error" },
      pattern: {
        value: validDecimalUptoTwoDigits,
        message: "valid-decimal-error",
      },
    },
    size: { xs: 12 },
  },
  {
    name: "max_musharakah_weight",
    formLabel: "musharakah-max-weight",
    placeholder: "musharakah-max-weight-placeholder",
    type: "number",
    size: { xs: 12 },
    tooltip: "musharakah-weight-tooltip",
    registerOptions: {
      required: {
        value: true,
        message: "musharakah-weight-required-error",
      },
      pattern: {
        value: isValidDecimal,
        message: "non-zero-amount-validation",
      },
    },
  },
];

export const editStoneShapeCutFields = [
  {
    type: "text",
    name: "name",
    formLabel: "shape-cut-name",
    placeholder: "shape-cut-name-placeholder",
    registerOptions: {
      required: { value: true, message: "shape-cut-name-required-error" },
      maxLength: { value: 100, message: "shape-cut-name-max-length-error" },
      validate: {
        noLeadingSpecialChar: (value) =>
          /^[a-zA-Z0-9]/.test(value) || "name-leading-special-char-error",
      },
    },
    size: { xs: 12 },
  },
];

export const addStoneClarityFields = [
  {
    name: "name",
    formLabel: "stone-clarity-name",
    placeholder: "stone-clarity-name-placeholder",
    type: "text",
    registerOptions: {
      required: { value: true, message: "stone-clarity-name-required-error" },
      maxLength: { value: 100, message: "stone-clarity-name-max-length-error" },
      validate: {
        noLeadingSpecialChar: (value) =>
          /^[a-zA-Z0-9]/.test(value) || "name-leading-special-char-error",
      },
    },
    size: { xs: 12 },
  },
];

export const riskLevelDefaultValues = {
  risk_level: "",
  equity_min: "",
  equity_max: "",
  penalty_amount: "",
  max_musharakah_weight: "",
  is_active: true,
};

export const musharakahMaterialsColumns = [
  { key: "material_item", label: "material-item" },
  { key: "carat_type", label: "carat-type" },
  { key: "status", label: "status" },
  { key: "action", label: "action" },
  { key: "edit", label: "edit" },
];

export const musharakahMaterialsFields = [
  {
    type: "autocomplete",
    name: "material_item",
    formLabel: "material-item",
    placeholder: "material-item-placeholder",
    size: { xs: 12 },
    registerOptions: {
      required: { value: true, message: "material-item-required-error" },
    },
  },
  {
    type: "autocomplete",
    name: "carat_type",
    formLabel: "carat-type",
    placeholder: "carat-type-placeholder",
    size: { xs: 12 },
    registerOptions: {
      required: { value: true, message: "carat-type-required-error" },
    },
  },
];
